import { motion } from "framer-motion";
import { MapPin, Clock, ShieldCheck } from "lucide-react";

const DOCTOR_IMG =
  "https://customer-assets.emergentagent.com/job_studio-ematologico/artifacts/6k48n1ib_IMG_3525.jpeg";

const TEXTURE_IMG =
  "https://images.unsplash.com/photo-1747673002516-f11a48cb0ce2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNhbG0lMjB3YXJtJTIwb3JnYW5pYyUyMHRleHR1cmV8ZW58MHx8fHwxNzgzODMyNjk5fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background texture */}
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          backgroundImage: `url(${TEXTURE_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(80px) saturate(0.7)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 -z-10 bg-[#F9F8F6]/60" aria-hidden />

      <div className="max-w-[1320px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Text */}
        <div className="lg:col-span-7">
          <motion.div
            initial="initial"
            animate="animate"
            transition={{ staggerChildren: 0.08, delayChildren: 0.05 }}
          >
            <motion.p variants={fadeUp} transition={{ duration: 0.6 }} className="eyebrow mb-6">
              Ematologia · Pavia · Voghera
            </motion.p>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="font-serif-display text-[42px] sm:text-[56px] lg:text-[76px] leading-[0.98] tracking-tight text-[#1A1D1A]"
            >
              Cura del sangue,
              <br />
              <span className="italic text-[#8A6D56]">ascolto</span> del paziente.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mt-8 max-w-[560px] text-[17px] leading-relaxed text-[#5C635E]"
            >
              Il <strong className="text-[#1A1D1A] font-semibold">Dott. Manuel Gotti</strong>,
              ematologo con esperienza clinica presso il Policlinico San Matteo di Pavia,
              offre visite specialistiche, diagnosi e consulenze per patologie del sangue —
              in un percorso costruito attorno alla persona.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#prenota"
                data-testid="hero-cta-prenota"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#8A6D56] text-white text-sm font-semibold hover:bg-[#735A47] transition-colors duration-200"
              >
                Prenota una visita
                <span aria-hidden>→</span>
              </a>
              <a
                href="#chi-sono"
                data-testid="hero-cta-chi-sono"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[#1A1D1A]/15 text-[#1A1D1A] text-sm font-semibold hover:bg-[#EFECE7] transition-colors duration-200"
              >
                Chi sono
              </a>
            </motion.div>

            {/* Trust markers */}
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-[640px]"
            >
              {[
                { icon: ShieldCheck, label: "Iscritto Ordine dei Medici", sub: "Provincia di Pavia" },
                { icon: MapPin, label: "Due sedi", sub: "Fondazione IRCCS Policlinico San Matteo · Studio Viqueria Voghera" },
                { icon: Clock, label: "Prenotazione online", sub: "Risposta in 24h" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3">
                  <item.icon className="w-5 h-5 text-[#8A6D56] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1D1A] leading-tight">{item.label}</p>
                    <p className="text-xs text-[#5C635E] mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#EFECE7]">
            <img
              src={DOCTOR_IMG}
              alt="Dott. Manuel Gotti, ematologo"
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" aria-hidden />
          </div>
          <div className="hidden md:block absolute -bottom-6 -left-6 max-w-[240px] bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-[0_8px_32px_rgba(26,29,26,0.06)]">
            <p className="eyebrow">In pratica dal</p>
            <p className="font-serif-display text-4xl mt-1 text-[#1A1D1A]">2008</p>
            <p className="text-xs text-[#5C635E] mt-1">
              Oltre 15 anni di esperienza in ematologia clinica.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
